/**
 * The door to the Operator pages: who may open them, and the trip to
 * onboarding for a person whose clone is not set up yet.
 *
 * `OperatorLayout` is the route element every `/operator/*` page renders
 * inside (routes.tsx). It asks GET /me once, through the gateway, and then:
 *
 *   admin, or a member with a seat   the page
 *   no clone yet                     onboarding, from Live only (the old
 *                                    portal's home page was its one gated page)
 *   signed in, no seat               one sentence saying so, and who to ask
 *   signed out                       nothing: `RequireAuth` is on its way to sign-in
 *   no service, or it did not answer the reason, with a way to try again
 *
 * Tidiness, like every gate in the console: the service refuses a person with
 * no seat whatever this draws, and the gateway refuses anybody it did not sign in.
 * Replaces the old portal's `OnboardingGate` and its own login redirect.
 *
 * `RequireOperatorAdmin` keeps a page to Operator admins (`me.user.role`),
 * as the old portal's middleware kept `/sandbox`: this app's own admin role
 * is not the question, the service's is.
 */
import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Button } from '../../../components/Button'
import { EmptyState, ErrorState, LoadingState } from '../../../components/States'
import { Page } from '../../../components/Page'
import { IconLock, IconRefresh, IconVideo } from '../../../components/icons'
import { SettingsSection } from '../../settings/SettingsSection'
import { NO_ACCESS_MESSAGE } from './api'
import {
  OPERATOR_ADMINS_ONLY_COPY,
  OPERATOR_ADMINS_ONLY_TITLE,
  accessFrom,
  sendsToOnboarding,
  type OperatorAccess as Access,
} from './access-model'
import { useOperatorMe } from './useOperatorMe'

/** Who may open the Operator pages, for the pages and the sidebar alike. */
export function useOperatorAccess(): Access & { retry: () => void } {
  const query = useOperatorMe()
  return { ...accessFrom(query), retry: () => void query.refetch() }
}

/** Where the gate draws a refusal: a page of its own, or a section of the Settings page. */
export type AccessFrame = 'page' | 'section'

function Framed({ frame, children }: { frame: AccessFrame; children: ReactNode }) {
  if (frame === 'section') {
    return (
      <SettingsSection title="Operator" description="The clone, its voice and face, its models and the meetings it joins.">
        {children}
      </SettingsSection>
    )
  }
  return (
    <Page icon={<IconVideo />} title="Operator">
      {children}
    </Page>
  )
}

/** The gate alone, for a caller that lays out its own content. */
export function OperatorAccess({ frame = 'page', children }: { frame?: AccessFrame; children: ReactNode }) {
  const access = useOperatorAccess()
  switch (access.state) {
    case 'allowed':
      return <>{children}</>
    case 'loading':
    case 'signed-out':
      return <LoadingState label="Opening Operator" />
    case 'no-access':
      return (
        <Framed frame={frame}>
          <EmptyState variant="card" icon={<IconLock />} title="Operator is not on this account" description={NO_ACCESS_MESSAGE} />
        </Framed>
      )
    case 'unavailable':
    case 'failed':
      return (
        <Framed frame={frame}>
          <ErrorState error={new Error(access.message)} title="Operator could not be opened">
            <div className="pt-1">
              <Button size="sm" variant="secondary" onClick={access.retry} leadingIcon={<IconRefresh className="h-3.5 w-3.5" />}>
                Try again
              </Button>
            </div>
          </ErrorState>
        </Framed>
      )
  }
}

/** Sends a person with no clone from Live to onboarding, as the old portal's home page did. Nothing else is gated. */
function OnboardingGate({ children }: { children: ReactNode }) {
  const access = useOperatorAccess()
  const location = useLocation()
  if (sendsToOnboarding(access, location.pathname)) return <Navigate to="/operator/onboarding" replace />
  return <>{children}</>
}

/**
 * A page for Operator admins only (the sandbox). Inside `OperatorLayout`, so
 * the person is already known: an admin sees the page, anybody else one
 * sentence in the page frame.
 */
export function RequireOperatorAdmin({ title = 'Operator', children }: { title?: string; children: ReactNode }) {
  const access = useOperatorAccess()
  if (access.state !== 'allowed') return <LoadingState label="Opening Operator" />
  if (access.me.user.role === 'admin') return <>{children}</>
  return (
    <Page icon={<IconVideo />} title={title}>
      <EmptyState variant="card" icon={<IconLock />} title={OPERATOR_ADMINS_ONLY_TITLE} description={OPERATOR_ADMINS_ONLY_COPY} />
    </Page>
  )
}

/** The route element: the gate, the onboarding trip, then the page. */
export function OperatorLayout({ frame = 'page' }: { frame?: AccessFrame }) {
  return (
    <OperatorAccess frame={frame}>
      <OnboardingGate>
        <Outlet />
      </OnboardingGate>
    </OperatorAccess>
  )
}
